import React, { Component, Fragment } from 'react';
import { getFormValues } from 'redux-form';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import moment from 'moment';
import _ from 'lodash';

import SelectSitesTable from 'Common/SelectSitesTable';
import { getIsDirty } from 'Common/Selectors/DirtySelector';
import { resetDirty } from 'State/dirtyTracking/actions';
import {
  NYSirsExportFieldEnum as Fields,
  NYSirsAutoExportFieldEnum as AutoFields,
} from 'Common/FieldEnum';
import {
  errorToast,
  createToast,
  toastType,
  toastError,
} from 'Common/ToastHelper';
import {
  NEW_YORK_SIRS_EXPORT_FORM_NAME,
  NEW_YORK_SIRS_AUTO_EXPORT_FORM_NAME,
} from 'Common/FormEnum';
import { DATE_FORMAT_FOR_UI } from 'Common/Constants';
import { Filter, Main } from 'Containers';
import { DirtyWrapper } from 'Components';
import { TitledCard } from 'Components/';

import * as Selectors from '../state/export/selectors';
import * as Actions from '../state/export/actions';
import { NewYorkSirsExportFormExport } from '../components/NewYorkSirsExportForm';
import { NewYorkSirsAutoExportFormExport } from '../components/NewYorkSirsAutoExportForm';

import 'Styles/pages/nysirsexport/nysirsexport.less';

class ExportHubContainer extends Component {
  static path = '/newyorksirsexport/:exportId';
  exportFormRef = React.createRef();

  state = {
    selectedSites: [],
  };

  getMainProps = () => {
    const { loading } = this.props;

    return {
      toolbox: {
        formName: NEW_YORK_SIRS_EXPORT_FORM_NAME,
        saveButtonProps: {
          content: 'Download',
          loading: loading,
          disabled: loading,
        },
        backButtonProps: {
          shown: true,
          disabled: loading,
          callback: () => this.goToExportsList(),
        },
        shown: true,
      },
    };
  };

  componentDidMount() {
    const {
      getAutoExportConfig,
      fetchSites,
      match: {
        params: { exportId },
      },
    } = this.props;

    if (+exportId) {
      getAutoExportConfig(exportId);
    }

    fetchSites().then(() => {
      this.setState({
        selectedSites: this.props.exportSites,
      });
    });
  }

  formatDate = (date) => {
    const noSlashDate = date.replace(/\//g, '').toString();
    return (
      noSlashDate.substring(4, 8) +
      noSlashDate.substring(0, 2) +
      noSlashDate.substring(2, 4)
    );
  };

  handleRetrieveClick = (formValues) => {
    const { fetchReport } = this.props;

    const model = {
      BedsCode: formValues[Fields.BedsCode],
      AsOfDate: this.formatDate(
        formValues[Fields.TodayOrOtherDate] === 0
          ? moment().format('MMDDYYYY')
          : formValues[Fields.AsOfDate]
      ),
      SiteIds: this.state.selectedSites,
      IncludeEligibilityTypeCode: formValues[Fields.IncludeEligibilityTypeCode],
    };

    fetchReport(model);
  };

  componentDidUpdate(prevProps, prevState) {
    const {
      updateConfigDidSave,
      clearReportHasData,
      reportHasData,
      clearErrors,
      configSaved,
      resetDirty,
    } = this.props;

    if (reportHasData !== undefined && !reportHasData) {
      createToast(toastType.info, 'Results', 'No data found!');
      clearReportHasData();
    }

    if (errorToast(this.props, prevProps, 'error')) {
      clearErrors();
    }

    if (configSaved) {
      createToast(toastType.success, 'Success', 'Configuration Saved');
      updateConfigDidSave(false);
      resetDirty();
      this.goToExportsList();
    }
  }

  handleSetExportConfig = () => {
    const {
      match: {
        params: { exportId },
      },
      configFormValues,
      reportFormValues,
      newBedsCode,
    } = this.props;

    if (this.exportFormRef.current.invalid) {
      //Ensure that validation errors displayed
      this.exportFormRef.current.submit();
      return;
    }

    if (!this.state.selectedSites.length) {
      return toastError('A site must be selected.');
    }

    const duplicateSites = _.filter(
      this.props.sites,
      (s) =>
        s.SirsExportId !== parseInt(exportId, 10) &&
        s.SirsExportId != null &&
        _.find(this.state.selectedSites, (ss) => ss === s.Id)
    );

    if (duplicateSites.length) {
      return toastError(
        'Following site(s) already assigned: ' +
          _.join(
            duplicateSites.map((s) => s.Description),
            ', '
          )
      );
    }

    const scheduleParams = {
      days: configFormValues.DayPicker,
      hour: isNaN(configFormValues.Hour) ? '' : configFormValues.Hour,
      min: isNaN(configFormValues.Minute) ? '' : configFormValues.Minute,
      amOrPm: configFormValues.AMorPM,
    };

    let importSchedule = this.craftSchedule(scheduleParams);

    const model = {
      Id: exportId,
      Enabled: configFormValues.EnabledToggle,
      FileName: configFormValues.FileName,
      ExportSchedule: importSchedule,
      SiteIds: this.state.selectedSites,
      BedsCode: newBedsCode,
      IncludeEligibilityTypeCode: reportFormValues.IncludeEligibilityTypeCode,
    };

    this.props.setAutoExportConfig(model);
  };

  craftSchedule = (params) => {
    let schedule = '';

    for (let i = 0; i < 7; i++) {
      if (params.days.includes(i.toString())) {
        schedule = schedule + 'Y';
      } else {
        schedule = schedule + 'N';
      }
    }

    if (params.amOrPm === 'AM' && params.hour === 12) {
      params.hour = 0;
    }

    if (params.amOrPm === 'PM' && params.hour !== 12) {
      params.hour = params.hour + 12;
    }

    let mins = params.min.toString();
    let hours = params.hour.toString();

    if (hours.length === 1) {
      hours = '0' + hours;
    }

    if (mins === '0') {
      mins = '00';
    }

    schedule = schedule + hours + mins;

    return schedule;
  };

  craftInitialTimeValues = (schedule) => {
    const dbDays = schedule.substring(0, 7);
    let dbHours = parseInt(schedule.substring(7, 9), 10);
    const dbMins = parseInt(schedule.substring(9, 11), 10);

    let amOrPm = dbHours < 12 ? 'AM' : 'PM';

    if (dbHours > 12) {
      dbHours = dbHours - 12;
    }
    if (dbHours === 0) {
      dbHours = 12;
    }

    let initialDays = [];

    for (let i = 0; i < 7; i++) {
      if (dbDays.charAt(i) === 'Y') {
        initialDays.push(i.toString());
      }
    }

    return {
      days: initialDays,
      hours: dbHours,
      mins: dbMins,
      amOrPm: amOrPm,
    };
  };

  goToExportsList = () => {
    this.props.resetConfig();
    this.props.history.replace('/newyorksirsexport');
  };

  onCancelClick = () => {
    this.goToExportsList();
  };

  handleSiteChange = (values) => {
    this.setState({
      selectedSites: values,
    });
  };

  render() {
    const {
      configFormValues,
      bedsCode,
      exportSchedule,
      configLoading,
      fileName,
      enabled,
      loading,
      sites,
      exportSites,
      assignedBedsCodes,
      usedFileNames,
      includeEligibilityTypeCode,
    } = this.props;

    let configTimeValues = {
      days: [],
      hours: '',
      mins: '',
      amOrPm: '',
    };
    if (exportSchedule && exportSchedule.length > 0) {
      configTimeValues = this.craftInitialTimeValues(exportSchedule);
    }

    return (
      <Fragment>
        <Filter title={'New York SIRS Export'} />
        <div className={'ny-sirs'}>
          <Main {...this.getMainProps()} />
        </div>
        <NewYorkSirsExportFormExport
          ref={this.exportFormRef}
          loading={loading}
          assignedBedsCodes={assignedBedsCodes}
          initialBedsCode={bedsCode}
          onSubmit={this.handleRetrieveClick}
          initialValues={{
            [Fields.TodayOrOtherDate]: 0,
            [Fields.AsOfDate]: moment().format(DATE_FORMAT_FOR_UI),
            [Fields.BedsCode]: bedsCode,
            [Fields.IncludeEligibilityTypeCode]: includeEligibilityTypeCode,
          }}
        />
        <TitledCard
          title={'Site Filters'}
          style={{ width: '60vw', minWidth: '700px' }}
        >
          {!configLoading && sites.length > 0 && (
            <SelectSitesTable
              fetchSites={false}
              sites={sites}
              onChange={this.handleSiteChange}
              initialSelectedSiteIds={exportSites}
            />
          )}
        </TitledCard>
        <DirtyWrapper closeModalYesCallback={this.onCancelClick}>
          {(showDirtyConfirm) => (
            <NewYorkSirsAutoExportFormExport
              onChangeConfig={showDirtyConfirm}
              onSubmit={() => this.handleSetExportConfig()}
              loading={configLoading}
              configFormValues={configFormValues}
              usedFileNames={usedFileNames}
              initialFileName={fileName}
              initialValues={{
                [AutoFields.FileName]: fileName,
                [AutoFields.EnabledToggle]: enabled,
                [AutoFields.DayPicker]: configTimeValues.days,
                [AutoFields.Hour]: configTimeValues.hours,
                [AutoFields.Minute]: configTimeValues.mins,
                [AutoFields.AmOrPm]: configTimeValues.amOrPm,
              }}
            />
          )}
        </DirtyWrapper>
      </Fragment>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  ownProps.setPageLevelDirty(getIsDirty()(state));

  return {
    configFormValues: getFormValues(NEW_YORK_SIRS_AUTO_EXPORT_FORM_NAME)(state),
    reportFormValues: getFormValues(NEW_YORK_SIRS_EXPORT_FORM_NAME)(state),
    exportSchedule: Selectors.getExportSchedule(state),
    reportHasData: Selectors.getReportHasData(state),
    configLoading: Selectors.getConfigLoading(state),
    configSaved: Selectors.getConfigSaved(state),
    bedsCode: Selectors.getBedsCode(state),
    includeEligibilityTypeCode: Selectors.getIncludeEligibilityCodeType(state),
    newBedsCode: Selectors.getNewBedsCode(state),
    loading: Selectors.getLoading(state),
    fileName: Selectors.getFileName(state),
    enabled: Selectors.getEnabled(state),
    error: Selectors.getErrors(state),
    sites: Selectors.getSites(state),
    exportSites: Selectors.getExportSites(state),
    assignedBedsCodes: Selectors.getAssignedBedsCodes(state),
    usedFileNames: Selectors.getUsedFileNames(state),
  };
};

const mapDispatchToProps = {
  resetDirty,
  ...Actions,
};

export const ExportHub = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ExportHubContainer)
);



// WEBPACK FOOTER //
// ./src/pages/newyorksirsexport/subpages/ExportHub.js